/**
 * viewing-over-time-by-devices-trend-chart
 *
 * @author    Andy Lowe <andy.lowe@mediatel.co.uk>
 * @copyright 2019 Mediatel Limited.
 */

"use strict";

import renderWeeklyViewingLevelsCSV from "./render-weekly-viewing-levels-csv";
import fileDownloader from "../common/file-downloader";
import onXHRClickButton from "./onxhr-click-button";

export default (dataUrl, settingOptions = {}) => sb => {
    let settings,
        form,
        downloadButton,
        devices = [],
        selectedDevices = [],
        streams = [],
        selectedStreams = [],
        selectedPeriods = [],
        errorMsg,
        submit,
        chartNote,
        chart;

    const maybeShowError = () => {
        const noBroadcasters = devices.length > 0 && selectedDevices.length < 1;
        const noStreams = streams.length > 0 && selectedStreams.length < 1;
        const noWeeks = sb.find('#period_week > option').length > 0 && selectedPeriods.length < 1;
        if (noBroadcasters || noWeeks || noStreams) {
            errorMsg.css("display", "block");
            submit[0].disabled = "disabled";
            downloadButton[0].disabled = "disabled";
        } else {
            errorMsg.css("display", "none");
            submit[0].disabled = null;
            downloadButton[0].disabled = null;
        }
    };

    const renderChart = data => {
        if (data.length < 1) {
            sb.empty(chart).append(chart, `<p>${settings.chart.errorMessage}</p>`);
            return;
        }

        chartNote.css("display", "block");

        const options = {
            chart: {type: "line", zoomType: "x"},
            title: {text: settings.chart.xLabel},
            tooltip: {
                formatter: function () {
                    var auditMsg = "";
                    var pointX = this.point.x;
                    var total = this.y.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
                    if (data.labels[pointX].length > 0) {
                        auditMsg = "<br>" + data.labels[pointX];
                    }
                    return this.x + "<br>● " + this.series.name + " <b>" + total + "</b> " + auditMsg;
                },
            },
            xAxis: {
                categories: data.categories,
                labels: {
                    rotation: -75,
                    step: data.series[0].data.length > 51 ? 4 : null
                }
            },
            yAxis: {
                min: 0,
                title: {text: settings.chart.yLabel}
            },
            plotOptions: {line: {marker: {enabled: false}}},
            series: data.series,
            credits: {enabled: false}
        };

        sb.chart(chart, options);
    };

    const downloadCSV = data => {
        fileDownloader(
            renderWeeklyViewingLevelsCSV(data),
            settings.csv.filename
        );
    };

    const showPleaseWait = () => {
        const report = sb.find("div.scroll_datagrid");
        sb.empty(report).append(report, "Please wait...");
    };

    const onFormSubmit = e => {
        e.preventDefault();
        const data = {
            method: "POST",
            device: selectedDevices,
            stream: selectedStreams,
            start_period: sb.find('#start_period_week')[0].value,
            end_period: sb.find('#end_period_week')[0].value,
            type: 'open'
        };

        showPleaseWait();

        sb.postApi(dataUrl, data).then(
            renderChart
        );
    };

    const onClickDownloadCSV = () => {
        showPleaseWait();
        const data = {
            method: "POST",
            device: selectedDevices,
            stream: selectedStreams,
            start_period: sb.find('#start_period_week')[0].value,
            end_period: sb.find('#end_period_week')[0].value,
            type: 'open'
        };

        sb.postApi(dataUrl, data).then(
            downloadCSV
        );
    };

    const initSettings = function initSettings() {
        settings = (typeof settingOptions !== "object") ? {} : settingOptions;
        settings.selectedLabel = settingOptions.selectedLabel || '';

        settings.chart = (typeof settingOptions.chart !== "object") ? {} : settingOptions.chart;
        settings.chart.xLabel = settingOptions.chart.xLabel || '';
        settings.chart.yLabel = settingOptions.chart.yLabel || '';
        settings.chart.errorMessage = settingOptions.chart.errorMessage || 'No data found';

        settings.csv = (typeof settingOptions.csv !== "object") ? {} : settingOptions.csv;
        settings.csv.filename = settingOptions.csv.filename || 'report.csv';
    };

    return {
        init() {
            initSettings();

            form = sb.find("form");
            errorMsg = form.query("div.invalid_input_message");
            submit = form.query("#button_submit");
            chart = sb.find(".chart");
            downloadButton = sb.find("#button_download_trigger");
            chartNote = sb.find("#chartNote");

            errorMsg.css("display", "none");

            sb.addEvent(form, "submit", onFormSubmit);
            sb.addEvent(downloadButton, "click", onClickDownloadCSV);

            sb.listen({
                devicesLoaded: loadedDevices => devices = loadedDevices,
                streamsLoaded: loadedStreams => streams = loadedStreams[0],
                devicesSelectionChanged: stns => {
                    selectedDevices = stns;
                    maybeShowError();
                },
                streamsSelectionChanged: stns => {
                    selectedStreams = stns[0];
                    maybeShowError();
                },
                periodsSelectionChanged: periods => {
                    selectedPeriods = periods;
                    maybeShowError();
                }
            });

            // start default report
            onXHRClickButton(submit[0]);
        },
        destroy() {
            sb.ignore([
                "devicesLoaded",
                "streamsLoaded",
                "devicesSelectionChanged",
                "streamsSelectionChanged",
                "periodsSelectionChanged"
            ]);

            sb.removeEvent(form, "submit", onFormSubmit);
            sb.removeEvent(downloadButton, "click", onClickDownloadCSV);

            form = null;
            errorMsg = null;
            submit = null;
            chart = null;
            chartNote = null;
        }
    }
};

